// Shared Social Security benefit formulas used across components

// FRA lookup table
const FRA_LOOKUP = {
  1937: { years: 65, months: 0 },
  1938: { years: 65, months: 2 },
  1939: { years: 65, months: 4 },
  1940: { years: 65, months: 6 },
  1941: { years: 65, months: 8 },
  1942: { years: 65, months: 10 },
  1943: { years: 66, months: 0 },
  1944: { years: 66, months: 0 },
  1945: { years: 66, months: 0 },
  1946: { years: 66, months: 0 },
  1947: { years: 66, months: 0 },
  1948: { years: 66, months: 0 },
  1949: { years: 66, months: 0 },
  1950: { years: 66, months: 0 },
  1951: { years: 66, months: 0 },
  1952: { years: 66, months: 0 },
  1953: { years: 66, months: 0 },
  1954: { years: 66, months: 0 },
  1955: { years: 66, months: 2 },
  1956: { years: 66, months: 4 },
  1957: { years: 66, months: 6 },
  1958: { years: 66, months: 8 },
  1959: { years: 66, months: 10 },
  1960: { years: 67, months: 0 }
};

export const getFra = (birthYear) => {
  if (birthYear <= 1937) return { years: 65, months: 0 };
  if (birthYear >= 1960) return { years: 67, months: 0 };
  return FRA_LOOKUP[birthYear] || { years: 67, months: 0 };
};

export const getFraYears = (birthYear) => {
  const fra = getFra(birthYear);
  return fra.years + (fra.months || 0) / 12;
};

// Pre-claim COLA application used by Early/Late view.
// If the claim age is in the future, apply COLA to the PIA before reduction/credits.
// Matches the behavior previously embedded in ShowMeTheMoneyCalculator.
// KNOWN ISSUE (deliberately deferred, see docs/superpowers/notes/2026-08-24-inflation-cola-model.md):
// this compounds by whole AGE-years (Math.floor(claimAgeYears) - 62), while
// projections.js's post-claim growth (benefitAfterClaim) compounds by whole CALENDAR-years
// (year - claimingCalendarYear). The two only agree for a January birthday -- for anyone else,
// sweeping filing month-by-month can show cumulative income dip right at a calendar-year
// boundary. Not a "which rate" problem (Kurt's single-flat-rate-for-the-whole-arc decision is
// correct and intentional, see the note) -- a "which calendar convention" inconsistency between
// this function and benefitAfterClaim.
export const preclaimColaFactor = (claimAgeYears, currentAgeYears, rate) => {
  if (claimAgeYears <= currentAgeYears) return 1;
  const pre60Years = Math.max(0, Math.min(60, claimAgeYears) - currentAgeYears);
  const colaYearsFrom62 = Math.max(0, Math.floor(claimAgeYears) - 62);
  return Math.pow(1 + rate, pre60Years + colaYearsFrom62);
};

export const monthsFromFra = (claimAgeYears, fraYears) => Math.round((claimAgeYears - fraYears) * 12);

// Standard SSA early filing reduction factors
// Input: monthsBeforeFra should be negative when before FRA
export const earlyReductionFactor = (monthsBeforeFra) => {
  const months = Math.abs(Math.min(0, monthsBeforeFra));
  const first36 = Math.min(36, months);
  const extra = Math.max(0, months - 36);
  const reduction = first36 * (5 / 9) / 100 + extra * (5 / 12) / 100;
  return Math.max(0, 1 - reduction);
};

// Standard SSA delayed retirement credit factor
// Input: monthsAfterFra should be >= 0 when after FRA
export const delayedRetirementCreditFactor = (monthsAfterFra) => {
  const months = Math.max(0, monthsAfterFra);
  return 1 + ((2 / 3) / 100) * months; // 2/3% per month
};

// Convenience helpers using ages in years (decimals)
export const earlyReductionFromAges = (filingAgeYears, fraYears) => {
  const monthsFromFra = Math.round((filingAgeYears - fraYears) * 12);
  return earlyReductionFactor(monthsFromFra);
};

export const drcIncreaseFromAges = (claimAgeYears, fraYears) => {
  const monthsFromFra = Math.round((claimAgeYears - fraYears) * 12);
  const base = delayedRetirementCreditFactor(monthsFromFra);
  // Return just the increase over PIA (i.e., factor - 1)
  return Math.max(0, base - 1);
};

// Monthly benefit at claim age using pre-claim COLA and SSA factors
export const monthlyBenefitAtClaim = ({ piaFRA, claimAgeYears, currentAgeYears, rate, fraYears }) => {
  const base = piaFRA * preclaimColaFactor(claimAgeYears, currentAgeYears, rate);
  const offset = monthsFromFra(claimAgeYears, fraYears);
  if (offset >= 0) return base * delayedRetirementCreditFactor(offset);
  return base * earlyReductionFactor(offset);
};

// Apply post-claim COLA for years after claim. Callers pass a CALENDAR-year delta
// (year - claimingCalendarYear, see projections.js) -- see the KNOWN ISSUE comment on
// preclaimColaFactor above for why that convention mismatch with pre-claim growth matters.
export const benefitAfterClaim = (baseMonthlyAtClaim, yearsAfterClaim, rate) => {
  const years = Math.max(0, yearsAfterClaim);
  return baseMonthlyAtClaim * Math.pow(1 + rate, years);
};

// Pre-claim COLA adjustment: treat provided PIA as at FRA.
// If claim is after FRA, apply COLA from FRA→claim; if at/before FRA, no pre-claim COLA.
export const adjustPIAForPreClaim = (piaAtFRA, claimAgeYears, fraYears, colaRate) => {
  if (claimAgeYears <= fraYears) return piaAtFRA;
  const yearsAfterFra = claimAgeYears - fraYears;
  return piaAtFRA * Math.pow(1 + colaRate, yearsAfterFra);
};

const benefitFormulas = {
  getFra,
  getFraYears,
  preclaimColaFactor,
  monthsFromFra,
  earlyReductionFactor,
  delayedRetirementCreditFactor,
  earlyReductionFromAges,
  drcIncreaseFromAges,
  monthlyBenefitAtClaim,
  benefitAfterClaim,
  adjustPIAForPreClaim
};

export default benefitFormulas;
